import { notifications } from '@mantine/notifications';

// Union type of all valid notification data-test-id values from towers project
export type NotificationTestId =
  // AutoBet Hook Notifications
  | 'autobet-stopped-max-failures'
  | 'bet-failed-attempt'
  | 'autobet-min-cap-reached'
  | 'autobet-max-cap-reached'
  | 'autobet-stopped-insufficient-balance'
  | 'autobet-completed-max-bets'
  | 'autobet-profit-target-reached'
  | 'autobet-stop-loss-reached'
  | 'autobet-summary'
  | 'error-no-session-token'
  | 'invalid-brick-selection'
  | 'insufficient-balance-bet'
  | 'info-autobet-already-active'
  | 'error-no-session-token-start'
  | 'error-pick-bricks-first'
  | 'insufficient-balance-start'
  | 'autobet-started'
  // Manual Bet Hook Notifications
  | 'manual-bet-failed'
  | 'manual-bet-no-token'
  // Start Manual Bet Hook Notifications
  | 'start-bet-no-token'
  // Manual Bet Cashout Hook Notifications
  | 'cashout-no-token'
  // Configure AutoBet Modal Notifications
  | 'autobet-config-saved';

// Type for notification options with enforced data-test-id
export interface TypeSafeNotificationOptions {
  title: string;
  message: string;
  color?: 'red' | 'orange' | 'blue' | 'green';
  autoClose?: number | false;
  dataTestId: NotificationTestId;
}

/**
 * Type-safe notification function that enforces valid data-test-id values
 *
 * @param options - Notification options with type-safe dataTestId
 *
 * @example
 * ```ts
 * showTypeSafeNotification({
 *   title: "AutoBet Started",
 *   message: "AutoBet session has begun",
 *   color: "blue",
 *   dataTestId: "autobet-started" // ✅ Type-safe - only valid IDs allowed
 * });
 *
 * showTypeSafeNotification({
 *   title: "Error",
 *   message: "Something went wrong",
 *   color: "red",
 *   dataTestId: "invalid-id" // ❌ TypeScript error - not in allowed list
 * });
 * ```
 */
export const showNotification = (options: TypeSafeNotificationOptions) => {
  const { dataTestId, ...notificationOptions } = options;

  notifications.show({
    ...notificationOptions,
    'data-test-id': dataTestId,
  });
};

/**
 * Type-safe notification function with pre-configured error styling
 *
 * @param options - Error notification options
 */
export const showErrorNotification = (
  options: Omit<TypeSafeNotificationOptions, 'color'>
) => {
  showNotification({
    ...options,
    color: 'red',
  });
};

/**
 * Type-safe notification function with pre-configured success styling
 *
 * @param options - Success notification options
 */
export const showSuccessNotification = (
  options: Omit<TypeSafeNotificationOptions, 'color'>
) => {
  showNotification({
    ...options,
    color: 'green',
  });
};

/**
 * Type-safe notification function with pre-configured info styling
 *
 * @param options - Info notification options
 */
export const showInfoNotification = (
  options: Omit<TypeSafeNotificationOptions, 'color'>
) => {
  showNotification({
    ...options,
    color: 'blue',
  });
};

/**
 * Type-safe notification function with pre-configured warning styling
 *
 * @param options - Warning notification options
 */
export const showWarningNotification = (
  options: Omit<TypeSafeNotificationOptions, 'color'>
) => {
  showNotification({
    ...options,
    color: 'orange',
  });
};
